/**
 * WebView Cache Management
 * Handles Android WebView-specific caching issues
 */

export const isRunningInWebView = () => {
  const ua = navigator.userAgent;
  return /Android/.test(ua) && /wv/.test(ua);
};

export const clearWebViewCache = () => {
  console.log('Clearing WebView cache...');

  // 1. Clear browser caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name).then(() => {
          console.log(`Cleared cache: ${name}`);
        });
      });
    });
  }

  // 2. Clear service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister().then(() => {
          console.log('Service worker unregistered');
        });
      });
    });
  }

  // 3. Clear IndexedDB
  if (window.indexedDB) {
    indexedDB.databases?.().then(dbs => {
      dbs.forEach(db => {
        indexedDB.deleteDatabase(db.name);
        console.log(`Cleared IndexedDB: ${db.name}`);
      });
    }).catch(() => {
      // Fallback for older browsers
      const knownDbs = ['travaio', 'app-cache', 'redux'];
      knownDbs.forEach(name => {
        try {
          indexedDB.deleteDatabase(name);
        } catch (e) {
          console.log(`Could not clear ${name}`);
        }
      });
    });
  }

  // 4. Clear LocalStorage except auth
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userInfo = localStorage.getItem('userInfo');

  localStorage.clear();
  sessionStorage.clear();

  if (token) localStorage.setItem('token', token);
  if (userId) localStorage.setItem('userId', userId);
  if (userInfo) localStorage.setItem('userInfo', userInfo);

  console.log('WebView cache cleared');
};

export const notifyNativeBridgeForClearCache = () => {
  // If your native app has a bridge to clear app-level cache
  if (window.Android && typeof window.Android.clearAppCache === 'function') {
    try {
      window.Android.clearAppCache();
      console.log('Native bridge: app cache cleared');
    } catch (e) {
      console.log('Native bridge not available or error:', e);
    }
  }
};

export const initWebViewCacheStrategy = () => {
  if (!isRunningInWebView()) {
    console.log('Not running in WebView, skipping WebView cache strategy');
    return;
  }

  console.log('Initializing WebView cache strategy...');

  // Strategy 1: Add query param to all fetch requests to bust cache
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    let [resource, config] = args;

    if (typeof resource === 'string' && !resource.includes('?')) {
      // Add cache buster to URLs without existing query params
      resource = resource + '?_cb=' + Date.now();
    } else if (typeof resource === 'string' && resource.includes('?')) {
      resource = resource + '&_cb=' + Date.now();
    }

    return originalFetch.call(this, resource, config);
  };

  // Strategy 2: Prevent aggressive WebView caching of HTML
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Cache-Control';
  meta.content = 'no-cache, no-store, must-revalidate';
  document.head.appendChild(meta);

  const meta2 = document.createElement('meta');
  meta2.httpEquiv = 'Pragma';
  meta2.content = 'no-cache';
  document.head.appendChild(meta2);

  const meta3 = document.createElement('meta');
  meta3.httpEquiv = 'Expires';
  meta3.content = '0';
  document.head.appendChild(meta3);

  console.log('WebView cache strategy initialized');
};

export const webViewHardRefresh = () => {
  console.log('Executing WebView hard refresh...');

  // Notify native app if it can clear app-level cache
  notifyNativeBridgeForClearCache();

  // Clear all web caches
  clearWebViewCache();

  // Force reload with cache bust
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now());
  window.location.href = url.toString();
};

// Global function for WebView
window.__webviewHardRefresh = webViewHardRefresh;
window.__isWebView = isRunningInWebView;
